// Considered: AtlasTextColumn — rejected because it renders a fixed, unbound
// text property with no kind-awareness (date/choice/multi-value formatting)
// and no absence handling; extending it would overload a component whose
// contract is "this sphere always has this exact string property". See spec
// Design System Reuse Audit / EAINT-857 Phase 4.
import React, { useEffect, useState } from 'react';
import { Sphere } from 'types/global';
import { SchemaProperty } from 'types/schema';
import { aspectKindFromSchemaProperty, type AspectKind } from '../../../../utils/aspectKind';
import { PermissionList } from '../../../../../types/sharedTypes';
import { useSchemaType } from '../../../../hooks/useSchemaType';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { useTranslation } from 'localization';
import ChipAtom from '../../../atoms/Chip/ChipAtom';
import SphereAvatar from '../../sphere-avatar/sphere-avatar';
import AtlasDateSelector from '../AtlasDateSelector/AtlasDateSelector';
import './AtlasSpherePropertyMolecule.scss';

/**
 * The renderable data kinds an aspect column dispatches on. `date`/`datetime` are kept
 * as distinct members (rather than collapsed to one) so the map below states, once, that
 * both route to the same renderer — see `kindToComponentMapIsSingleSourceOfTruth`.
 * `text` is also the fallback for any kind this molecule doesn't otherwise recognise
 * (`unknownKindFallsBackToTextRender`) — an unrecognised aspect still renders its label
 * rather than going blank.
 */
export { ASPECT_KINDS, type AspectKind } from '../../../../utils/aspectKind';

export interface AtlasSpherePropertyMoleculeProps {
  /** The sphere whose bound aspect is rendered. */
  sphere: Sphere;
  /** The aspect's `system_name` on the sphere's schema. */
  propertyKey: string;
  /** Relative ("2 days ago") vs. absolute date formatting, for `type: 'date'` aspects. */
  isFriendly?: boolean;
  /** How a single-value choice aspect renders: a coloured chip or plain text. */
  choiceStyle?: 'chip' | 'text';
  /** For a multi-value aspect, how many values render before collapsing into a `+N` chip. */
  maxValues?: number;
  /** For a multi-value aspect, what stands in for the values beyond `maxValues`: a `+N` chip, or nothing. */
  overflow?: 'count' | 'none';
  /** For a `type: 'reference'` aspect, whether the referenced sphere's avatar renders alongside its name. */
  showAvatar?: boolean;
  /**
   * Translation key shown (instead of an avatar + resolved name) when a `type: 'reference'`
   * value isn't a resolvable sphere id — e.g. a legacy non-UUID author value. Additive: only
   * author-field columns pass it; every other reference column keeps rendering the raw id
   * with no label when unresolvable (EAINT-899 SC7).
   */
  fallbackLabelKey?: string;
  /** For a `type: 'number'` aspect, how the value renders: raw, a filled bar, or a percentage. */
  numberFormat?: 'value' | 'bar' | 'percent';
  /**
   * For a `text`-kind aspect, the number of characters shown before truncating with
   * an expand/collapse affordance — the same option `AtlasTextColumn` carried
   * (default matches its `characterLimit = 100`) before the two collapsed onto this
   * molecule (EAINT-866 Phase 3).
   */
  characterLimit?: number;
  /**
   * Required so every call site is forced to wire the same permission seam a `date`-kind
   * aspect needs to render `AtlasDateSelector` (an editable control, not a read-only
   * `DateDisplay`) — see ADR-027. Only kind `date`/`datetime` reads this; every other
   * kind renders read-only and ignores it.
   */
  fetchPermissionList: (
    sphereId: string,
    nodeLabel: string,
    permissions?: string[]
  ) => Promise<PermissionList | null>;
  /** Required alongside `fetchPermissionList` — commits an edited `date`-kind aspect. Ignored by every other kind. */
  onDateChanged: (sphereId: string, propertyName: string, newValue: string | null) => Promise<void>;
  className?: string;
  style?: React.CSSProperties;
}

/** Resolves a raw stored value to its display label via the property's `metadata.options`, when present. */
function resolveOptionLabel(propertyDef: SchemaProperty | undefined, rawValue: unknown): string {
  const options = propertyDef?.metadata?.options as Array<{ label: string; value: string }> | undefined;
  const match = options?.find((option) => option.value === rawValue);
  return match?.label ?? String(rawValue);
}


/** Everything a kind's renderer needs — computed once per render, dispatched by kind. */
interface AspectRenderContext {
  sphere: Sphere;
  propertyKey: string;
  rawValue: unknown;
  propertyDef: SchemaProperty | undefined;
  label: string;
  isFriendly: boolean;
  choiceStyle: 'chip' | 'text';
  showAvatar: boolean;
  numberFormat: 'value' | 'bar' | 'percent';
  characterLimit: number;
  referencedName?: string;
  /** Set when a `reference` value isn't a resolvable sphere id — see `isSphereIdLike`. */
  isUnresolvableReference?: boolean;
  fallbackLabel?: string;
  fetchPermissionList: AtlasSpherePropertyMoleculeProps['fetchPermissionList'];
  onDateChanged: AtlasSpherePropertyMoleculeProps['onDateChanged'];
  className: string;
  style?: React.CSSProperties;
}

/**
 * `choice` (text style) converges on the same plain-label cell the fallback path uses.
 *
 * The chip test asks the shared resolver rather than re-testing `metadata.options`: this
 * function is reached for BOTH the choice and text kinds, so it has to re-establish which
 * one it is — and doing that with its own check was a fourth private kind decision, the
 * exact thing OQ-7 was about.
 */
function renderChoiceOrTextAspect(ctx: AspectRenderContext): JSX.Element {
  const isChoice = aspectKindFromSchemaProperty(ctx.propertyDef, ctx.rawValue) === 'choice';
  if (isChoice && ctx.choiceStyle === 'chip') {
    return (
      <div className={`atlas-sphere-property atlas-sphere-property--choice ${ctx.className}`} style={ctx.style}>
        <ChipAtom label={ctx.label} />
      </div>
    );
  }
  return (
    <div className={`atlas-sphere-property ${ctx.className}`} style={ctx.style}>
      <span className="atlas-sphere-property__value">{ctx.label}</span>
    </div>
  );
}

/**
 * The `text` kind's cell — carries the truncate-at-`characterLimit` + click-to-expand
 * behaviour `AtlasTextColumn` had (EAINT-866 Phase 3 folds that type in here). Its own
 * component (not a plain render function) because the expand/collapse toggle is local,
 * per-cell state — `renderChoiceOrTextAspect` and its siblings are stateless functions.
 */
function TextAspectCell({
  label,
  characterLimit,
  className,
  style,
}: Readonly<{
  label: string;
  characterLimit: number;
  className: string;
  style?: React.CSSProperties;
}>): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const isTruncatable = label.length > characterLimit;
  const displayText = isTruncatable && !expanded ? `${label.substring(0, characterLimit)}...` : label;

  // A truncatable cell is a real <button>, not a div wearing `role="button"`: focus,
  // Enter/Space activation and the announced role all come from the element, so the
  // hand-rolled tabIndex + onKeyDown pair below it can go. `aria-expanded` states which
  // way the toggle currently sits — the old version announced "button" and nothing else.
  if (!isTruncatable) {
    return (
      <div className={`atlas-sphere-property atlas-sphere-property--text ${className}`} style={style}>
        <span className="atlas-sphere-property__value">{displayText}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`atlas-sphere-property atlas-sphere-property--text atlas-sphere-property--clickable ${className}`}
      style={style}
      onClick={() => setExpanded((prev) => !prev)}
      aria-expanded={expanded}
    >
      <span className="atlas-sphere-property__value">{displayText}</span>
    </button>
  );
}

function renderTextAspect(ctx: AspectRenderContext): JSX.Element {
  return (
    <TextAspectCell
      label={ctx.label}
      characterLimit={ctx.characterLimit}
      className={ctx.className}
      style={ctx.style}
    />
  );
}

function renderDateAspect(ctx: AspectRenderContext): JSX.Element {
  return (
    <AtlasDateSelector
      sphere={ctx.sphere}
      dateProperty={ctx.propertyKey}
      isFriendly={ctx.isFriendly}
      fetchPermissionList={ctx.fetchPermissionList}
      onDateChanged={ctx.onDateChanged}
      className={`atlas-sphere-property atlas-sphere-property--date ${ctx.className}`}
      style={ctx.style}
    />
  );
}

function renderReferenceAspect(ctx: AspectRenderContext): JSX.Element {
  if (ctx.isUnresolvableReference) {
    return (
      <div
        className={`atlas-sphere-property atlas-sphere-property--reference atlas-sphere-property--reference-unresolvable ${ctx.className}`}
        style={ctx.style}
      >
        {ctx.fallbackLabel && (
          <span className="atlas-sphere-property__system-label">{ctx.fallbackLabel}</span>
        )}
        <span className="atlas-sphere-property__value">{ctx.rawValue as string}</span>
      </div>
    );
  }

  const referenceLabel = ctx.referencedName ?? (ctx.rawValue as string);
  return (
    <div className={`atlas-sphere-property atlas-sphere-property--reference ${ctx.className}`} style={ctx.style}>
      {ctx.showAvatar && (
        <SphereAvatar
          sphereId={ctx.rawValue as string}
          sphereName={referenceLabel}
          size="xsmall"
          className="atlas-sphere-property__avatar"
        />
      )}
      <span className="atlas-sphere-property__value">{referenceLabel}</span>
    </div>
  );
}

function renderNumberAspect(ctx: AspectRenderContext): JSX.Element {
  const rawValue = ctx.rawValue as number;
  if (ctx.numberFormat === 'bar') {
    const clamped = Math.max(0, Math.min(100, rawValue));
    return (
      <div className={`atlas-sphere-property atlas-sphere-property--number-bar ${ctx.className}`} style={ctx.style}>
        {/*
          Decorative: the bar restates, visually, the number rendered beside it. Giving it
          `role="progressbar"` made assistive tech announce the same value twice, and this
          is a data cell rather than task progress.
        */}
        <div className="atlas-sphere-property__bar-track" aria-hidden="true">
          <div className="atlas-sphere-property__bar-fill" style={{ width: `${clamped}%` }} />
        </div>
        <span className="atlas-sphere-property__value">{rawValue}</span>
      </div>
    );
  }
  if (ctx.numberFormat === 'percent') {
    return (
      <div className={`atlas-sphere-property atlas-sphere-property--number-percent ${ctx.className}`} style={ctx.style}>
        <span className="atlas-sphere-property__value">{`${rawValue}%`}</span>
      </div>
    );
  }
  // 'value' renders identically to the generic label cell.
  return renderChoiceOrTextAspect(ctx);
}

/**
 * Exported for the vocabulary guard test: `Record<AspectKind, ...>` makes this and
 * `ASPECT_KINDS` provably identical at compile time, and the renderer below reads only
 * this map — no other branch decides which component a kind draws
 * (`kindToComponentMapIsSingleSourceOfTruth`). Presentation-only: no permission import,
 * no permission logic — `fetchPermissionList`/`onDateChanged` are threaded through the
 * context object, not read here.
 */
export const ASPECT_KIND_RENDERERS: Record<AspectKind, (ctx: AspectRenderContext) => JSX.Element> = {
  date: renderDateAspect,
  datetime: renderDateAspect,
  reference: renderReferenceAspect,
  number: renderNumberAspect,
  choice: renderChoiceOrTextAspect,
  text: renderTextAspect,
};

const DEFAULT_MAX_VALUES = 2;

const AtlasSpherePropertyMolecule: React.FC<AtlasSpherePropertyMoleculeProps> = ({
  sphere,
  propertyKey,
  isFriendly = true,
  // Wireframe ASPECT_KIND_OPTIONS.choice declares `v: "chip"`. An earlier pass defaulted
  // to 'text', which is a quieter cell but not what the design specifies — a choice value
  // is categorical, and the chip is what makes that legible at a glance.
  choiceStyle = 'chip',
  maxValues = DEFAULT_MAX_VALUES,
  overflow = 'count',
  // Wireframe ASPECT_KIND_OPTIONS.reference declares `v: true`.
  showAvatar = true,
  numberFormat = 'value',
  // Matches `AtlasTextColumn`'s prior default (EAINT-866 Phase 3).
  characterLimit = 100,
  fallbackLabelKey,
  fetchPermissionList,
  onDateChanged,
  className = '',
  style,
}) => {
  const { t } = useTranslation();
  const schemaType = Array.isArray(sphere?.properties?.type)
    ? sphere.properties.type[0]
    : sphere?.properties?.type;
  const { schema: matchingSchema } = useSchemaType(schemaType ?? null);
  const propertyDef = matchingSchema?.properties.find(
    (p: SchemaProperty) => p.name === propertyKey
  );

  const rawValue = sphere?.properties?.[propertyKey];
  const isEmpty =
    rawValue === null ||
    rawValue === undefined ||
    rawValue === '' ||
    (Array.isArray(rawValue) && rawValue.length === 0);

  // Reference aspects store the target sphere's id; the display label
  // (defaults to that sphere's `name`, matching `ReferencePropertyMetadata`)
  // is resolved through the canonical avatar cache — never a direct
  // `getSignedSphereImage` call (see /standards/frontend/sphere-avatar.md).
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient;
  const [referencedName, setReferencedName] = useState<string | undefined>(undefined);
  // Set once resolution SETTLES without a usable name — a legacy non-sphere-id value
  // (`'migration:has-factor-reanchor'`, an adUserId — `backfillAttributeInstances
  // ToAspectAttachments.ts:74-76`) or a genuine cache miss. `globalSphereCache.
  // getSphereData` already short-circuits non-UUID ids to a nameless result rather
  // than throwing, so this is reached via the `.then()` branch, not `.catch()`, for
  // the common case — but a real fetch failure is tolerated the same way (SC7).
  const [referenceUnresolvable, setReferenceUnresolvable] = useState(false);
  const isReference = propertyDef?.type === 'reference' && typeof rawValue === 'string';

  useEffect(() => {
    let isMounted = true;
    // Reset before resolving: Atlas virtualizes its rows, so this instance is reused
    // for a different sphere/property and would otherwise render the PREVIOUS row's
    // resolution until the new one settles — a resolvable author flashing the
    // "System" fallback plus a raw UUID.
    setReferencedName(undefined);
    setReferenceUnresolvable(false);
    if (isReference && graphClient) {
      globalSphereCache
        .getSphereData(rawValue as string, graphClient)
        .then((data) => {
          if (!isMounted) return;
          const name = data.info?.sphere?.properties?.name ?? data.info?.properties?.name;
          setReferencedName(name);
          setReferenceUnresolvable(!name);
        })
        .catch(() => {
          if (!isMounted) return;
          // Render the raw id (with the fallback label, when provided) rather than crash the cell.
          setReferenceUnresolvable(true);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [isReference, rawValue, graphClient]);

  if (isEmpty) {
    return (
      <div className={`atlas-sphere-property atlas-sphere-property--empty ${className}`} style={style}>
        <span aria-hidden="true">—</span>
      </div>
    );
  }

  if (Array.isArray(rawValue)) {
    const visible = rawValue.slice(0, maxValues);
    const overflowCount = rawValue.length - visible.length;
    return (
      <div className={`atlas-sphere-property atlas-sphere-property--multi ${className}`} style={style}>
        {visible.map((value, index) => (
          <span key={`${String(value)}-${index}`} className="atlas-sphere-property__value">
            {resolveOptionLabel(propertyDef, value)}
          </span>
        ))}
        {overflow === 'count' && overflowCount > 0 && (
          <ChipAtom
            className="atlas-sphere-property__overflow"
            label={`+${overflowCount}`}
          />
        )}
      </div>
    );
  }

  const kind = aspectKindFromSchemaProperty(propertyDef, rawValue);
  return ASPECT_KIND_RENDERERS[kind]({
    sphere,
    propertyKey,
    rawValue,
    propertyDef,
    label: resolveOptionLabel(propertyDef, rawValue),
    isFriendly,
    choiceStyle,
    showAvatar,
    numberFormat,
    characterLimit,
    referencedName,
    isUnresolvableReference: isReference && referenceUnresolvable,
    fallbackLabel: fallbackLabelKey ? t(fallbackLabelKey) : undefined,
    fetchPermissionList,
    onDateChanged,
    className,
    style,
  });
};

export default AtlasSpherePropertyMolecule;
